import { inject, Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';

import { GcdmCustomerService } from '../clients/gcdm/gcdm-customer.service';
import { RetailCrmService } from '../clients/retail-crm/retail-crm.service';
import { Customer } from '../model/customer.model';
import { ExtendedCustomer } from '../model/extended-customer.model';

/**
 * The outcome of a two-step sync.
 *
 * `RETAIL_LINK_FAILED` is not an error but a partial success: the customer *does* exist in the
 * NSC Database under the returned GCID. Callers must surface it and offer a retry that reuses
 * that GCID, otherwise a second attempt would create a duplicate business partner.
 */
export type GcdmSyncResult =
  | { status: 'SUCCESS'; gcid: string; accountNumber?: string }
  | { status: 'RETAIL_LINK_FAILED'; gcid: string; error: unknown };

/**
 * Orchestrates the write flows of the GCDM migration (CUSTAPP-611).
 *
 * All of them are two steps - write to the NSC Database, then tell Retail CRM about it - and
 * there is no distributed transaction, hence the explicit partial-failure result.
 *
 * | Case | Flow                                                        |
 * |------|-------------------------------------------------------------|
 * | A1   | create in GCDM, then import into Retail CRM by GCID          |
 * | B1   | create in GCDM, then link the existing Retail CRM account    |
 * | B2   | link the existing Retail CRM account to an existing GCID     |
 */
@Injectable({
  providedIn: 'root',
})
export class GcdmSyncService {
  private readonly gcdmCustomerService = inject(GcdmCustomerService);
  private readonly retailCrmService = inject(RetailCrmService);
  private readonly logger = inject(NGXLogger);

  /**
   * Case A1: a new customer created with the NSC sync option enabled.
   * Creates the customer in the NSC Database and imports it into Retail CRM.
   *
   * @returns the GCID and the Retail CRM account number created by the import
   */
  async createSyncedCustomer(customer: Customer): Promise<GcdmSyncResult> {
    const gcid = await this.gcdmCustomerService.createCustomer(customer);

    try {
      const accountNumber = await this.retailCrmService.importFromGcdm(gcid);
      return { status: 'SUCCESS', gcid, accountNumber };
    } catch (error) {
      return this.retailLinkFailed(gcid, 'import the new NSC Database customer into Retail CRM', error);
    }
  }

  /**
   * Case B1: an existing local Retail CRM customer with no NSC Database record.
   * Creates the customer in the NSC Database and links the existing account to it.
   */
  async syncNewGcdmCustomer(accountNumber: string, customer: ExtendedCustomer): Promise<GcdmSyncResult> {
    const gcid = await this.gcdmCustomerService.createCustomerFromExtendedCustomer(customer);

    try {
      await this.retailCrmService.syncWithGcdm(accountNumber, gcid);
      return { status: 'SUCCESS', gcid, accountNumber };
    } catch (error) {
      return this.retailLinkFailed(gcid, 'link the new NSC Database customer to Retail CRM', error);
    }
  }

  /**
   * Case B2(CUSTAPP-611): an existing local Retail CRM customer matched to an existing NSC Database record.
   * Writes the merged data back to the NSC Database, then links the two.
   *
   * The GCDM update comes first on purpose: if it fails, nothing has changed anywhere and the
   * caller can simply show the error. The reverse order would leave Retail CRM pointing at a
   * GCID whose data the user never approved.
   */
  async updateAndLink(accountNumber: string, gcid: string, customer: ExtendedCustomer): Promise<GcdmSyncResult> {
    await this.gcdmCustomerService.updateCustomer(gcid, customer);

    try {
      await this.retailCrmService.syncWithGcdm(accountNumber, gcid);
      return { status: 'SUCCESS', gcid, accountNumber };
    } catch (error) {
      return this.retailLinkFailed(gcid, 'link the existing NSC Database customer to Retail CRM', error);
    }
  }

  private retailLinkFailed(gcid: string, action: string, error: unknown): GcdmSyncResult {
    this.logger.error(
      `#CDFTF0269 The customer was written to the NSC Database under GCID ${gcid}, but CDF could not ${action}. ` +
        `Any retry must reuse this GCID to avoid creating a duplicate.`,
      error,
    );
    return { status: 'RETAIL_LINK_FAILED', gcid, error };
  }
}
